import { FEATURED_OFFER } from '../../data/offers'
import { useSection } from '../../cms/cms-context'
import { useGsapContext } from '../../hooks/useGsapReveal'
import {
  parallax,
  splitLinesReveal,
  staggerChildren,
} from '../../animations/gsapAnimations'
import Button from '../ui/Button'

/* One cinematic band, one offer, one action. On the dark theme the
   photograph can finally carry the copy directly: the scrim is the page's
   own black rising into the frame, so the car keeps its highlights and the
   words keep their contrast. The plate drifts against the scroll — the one
   deep layer this page allows itself outside the hero. */
function SpecialOffer() {
  /* The campaign in data/offers is still the default — what this band says
     with nothing set in the console, and what it falls back to when the API
     cannot be reached. */
  const offer = FEATURED_OFFER
  const cms = useSection()

  const scope = useGsapContext<HTMLElement>((element) => {
    const plate = element.querySelector('[data-offer-plate]')
    if (plate) {
      parallax(plate, {
        trigger: element,
        amount: 12,
        start: 'top bottom',
        end: 'bottom top',
      })
    }

    staggerChildren(element, '[data-offer]', { y: 26, stagger: 0.1 })

    /* The headline rises out of its mask over the photograph rather than
       fading like the copy around it — the one line here that is a claim. */
    const title = element.querySelector<HTMLElement>('[data-offer-title]')
    return title ? splitLinesReveal(title, { delay: 0.15 }) : undefined
  })

  return (
    <section
      ref={scope}
      aria-labelledby="offer-title"
      className="relative overflow-hidden border-y border-line-soft"
    >
      {/* ---------- the picture ---------- */}
      <div aria-hidden="true" className="absolute inset-0">
        <div data-offer-plate className="absolute inset-x-0 -inset-y-[8%]">
          <img
            src={cms.text(
              'image',
              'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1800&q=75',
            )}
            alt=""
            loading="lazy"
            decoding="async"
            className="size-full object-cover object-[center_60%]"
          />
        </div>
        <div className="absolute inset-0 bg-linear-to-r from-void/95 via-void/70 to-void/25" />
        <div className="absolute inset-x-0 top-0 h-24 bg-linear-to-b from-void to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-linear-to-t from-void to-transparent" />
      </div>

      {/* ---------- the offer ---------- */}
      <div className="relative mx-auto flex w-full max-w-[1320px] flex-col items-start gap-6 px-5 py-24 sm:px-8 sm:py-32">
        <p
          data-offer
          {...cms.field('eyebrow')}
          className="rounded-full border border-white/25 bg-white/10 px-4 py-2 label text-lime-lift backdrop-blur-md"
        >
          {cms.text('eyebrow', offer.eyebrow)}
        </p>
        <h2
          id="offer-title"
          data-offer-title
          {...cms.field('title')}
          className="max-w-[16ch] font-display text-display-2 text-white"
        >
          {cms.text('title', offer.title)}
        </h2>
        <p
          data-offer
          {...cms.field('lede')}
          className="max-w-[50ch] text-[15.5px] leading-relaxed text-white/80"
        >
          {cms.text('lede', offer.body)}
        </p>
        <div data-offer className="mt-2 flex flex-wrap gap-3">
          <Button to={cms.text('ctaHref', offer.href)} size="lg" arrow magnetic {...cms.field('cta')}>
            {cms.text('cta', offer.cta)}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            to={cms.text('secondaryHref', '/lease-calculator')}
            {...cms.field('secondaryCta')}
          >
            {cms.text('secondaryCta', 'Calculate lease')}
          </Button>
        </div>
      </div>
    </section>
  )
}

export default SpecialOffer
